import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from './useWebSocket';
import { playerConfig } from '../config';

export interface UseViewerCountReturn {
  /** Current viewer count, null if not yet received */
  count: number | null;
  /** Whether the WebSocket is connected */
  connected: boolean;
}

const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * Hook for real-time viewer count via WebSocket (Req 1.14).
 *
 * - Connects to the viewer count WebSocket endpoint
 * - Sends a heartbeat every 30 seconds to maintain presence
 * - Receives `{ type: "viewer_count", count: N }` messages
 */
export function useViewerCount(): UseViewerCountReturn {
  const [count, setCount] = useState<number | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const url = buildWebSocketUrl(playerConfig.wsViewerCountUrl);

  const handleMessage = useCallback((data: unknown) => {
    if (isViewerCountMessage(data)) {
      setCount(data.count);
    }
  }, []);

  const { connected, send } = useWebSocket({
    url,
    onMessage: handleMessage,
    autoConnect: !!playerConfig.wsViewerCountUrl,
  });

  // Send heartbeat every 30s while connected
  useEffect(() => {
    if (connected) {
      // Send initial heartbeat on connect
      send({ type: 'heartbeat' });

      heartbeatRef.current = setInterval(() => {
        send({ type: 'heartbeat' });
      }, HEARTBEAT_INTERVAL_MS);
    }

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [connected, send]);

  return { count, connected };
}

function buildWebSocketUrl(baseUrl: string): string {
  if (!baseUrl) return '';
  const channelId = playerConfig.channelId;
  const token = playerConfig.wsToken;
  return `${baseUrl}?token=${encodeURIComponent(token)}&channelId=${encodeURIComponent(channelId)}`;
}

interface ViewerCountMessage {
  type: 'viewer_count';
  count: number;
}

function isViewerCountMessage(data: unknown): data is ViewerCountMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>).type === 'viewer_count' &&
    typeof (data as Record<string, unknown>).count === 'number'
  );
}
